import { Fragment, HTMLAttributes, ReactNode, memo } from 'react';
import { Button, Popover, Tooltip } from 'antd';
import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import detailSvg from 'views/Aide/assets/more.svg';
import TradePaymentAmountLazada, { DetailItem } from './TradeDetailPaymentLazada';
import { TradeDetailPaymentTikTok } from './TradeDetailPaymentTikTok';
import TradeDetailIncomeLazada from './TradeDetailIncomeLazada';
import { TradeDetailPaymentKwai } from './TradeDetailPaymentKwai';
import TradePaymentAmountShopee from './TradeDetailPaymentShopee';
import TradeDetailIncomeShopee from './TradeDetailIncomeShopee';
import TradeDetailIncomeMercado from './TradeDetailIncomeMercado';
import { LogisticsContent } from './styles';
import SendLogistics from './SendLogistics';
import { Trigger as PopoverTrigger } from './TradeRefund';
import MercadoLogistics from './MercadoLogistics';
import Copy from './Copy';
import {
  TradeDetailAmountBlock,
  TradeDetailBlock,
  TradeDetailLogisticsBlock,
  TradeDetailTextBlock,
} from './useTradeDetailBusiness';
import { DetailImg, LogisticsDetailItem, LogisticsItem, LogisticsSend } from './styles';
import { useLogisticsListBusiness } from './useLogisticsListBusiness';
import { LogisticsPackage, getTranslation } from './useTradeTranslate';
import { ToolbarIcon } from '../Chat/components/MessageEditor/ToolbarIcon';
import { SendLogisticsInfo } from '../components/SendLogisticsInfo';
import { MediaViewer, MediaItem } from 'components/MediaViewer';
import { css, cx } from '@emotion/css';
import { Send } from 'lucide-react';
import { useSendFileImage } from 'views/Conversation/Chat/components/MessageEditor/Toolbar/ImageUpload';

export interface TradeDetailClassicViewProps extends HTMLAttributes<HTMLElement> {
  trade: Conversation.Trade.TradeWithLogistics;
  blocks: TradeDetailBlock[];
  storeType?: Conversation.Trade.IStoreType;
  conversation?: Conversation.Conversation;
}

const renderAmountBlock = (
  block: TradeDetailAmountBlock,
  trade: Conversation.Trade.TradeWithLogistics,
  storeType?: Conversation.Trade.IStoreType,
) => {
  switch (block.variant) {
    case 'payment_lazada':
      return <TradePaymentAmountLazada key={block.id} trade={trade} storeType={storeType} />;
    case 'payment_tiktok':
      return <TradeDetailPaymentTikTok key={block.id} trade={trade} defaultFold />;
    case 'income_lazada':
      return <TradeDetailIncomeLazada key={block.id} trade={trade} />;
    case 'income_kwai':
      return <TradeDetailPaymentKwai key={block.id} trade={trade} />;
    case 'income_shopee':
      return (
        <Fragment key={block.id}>
          <TradePaymentAmountShopee trade={trade} />
          <TradeDetailIncomeShopee trade={trade} />
        </Fragment>
      );
    case 'income_mercado':
      return <TradeDetailIncomeMercado key={block.id} trade={trade} />;
    default:
      return null;
  }
};

const renderTextBlock = (block: TradeDetailTextBlock) => {
  return (
    <DetailItem key={block.id}>
      <span>{block.label}</span>
      <span>{block.value}</span>
    </DetailItem>
  );
};

const renderLogisticsContent = (block: TradeDetailLogisticsBlock): ReactNode => {
  const { payload } = block;

  if (payload.kind === 'common') {
    return <LogisticsContent platform="SHOPEE" logisticsPackageList={payload.logisticsList} />;
  }

  if (payload.kind === 'commonThird') {
    return (
      <LogisticsContent platform="SHOPEE" logisticsPackageList={payload.logisticsList} isThird />
    );
  }

  if (payload.kind === 'hisense') {
    return (
      <LogisticsContent platform="SHOPEE" logisticsPackageList={payload.logisticsList} isThird />
    );
  }

  if (payload.kind === 'lazada') {
    return <LogisticsContent platform="LAZADA" logisticsPackageList={payload.logisticsList} />;
  }

  return <MercadoLogistics logistics={payload.logistics} sellerOrderUrl={payload.sellerOrderUrl} />;
};

const LogisticsDetailRow = ({
  row,
}: {
  row: NonNullable<TradeDetailLogisticsBlock['detailRows']>[number];
}) => {
  const { t } = useTranslate();

  return (
    <LogisticsDetailItem>
      <span>{row.label}</span>
      <span title={row.value}>
        <span
          className={cx('logistics-detail-value-text', {
            'logistics-detail-value-text--multiline': row.id === 'shippingCarrier',
          })}
          title={row.value}
        >
          {row.value}
        </span>
        {row.copyable ? (
          <Copy
            text={row.value}
            successMessage={t('common.feedback.copySuccess')}
            failedMessage={t('common.feedback.copyFailed')}
          />
        ) : null}
      </span>
    </LogisticsDetailItem>
  );
};

const getLatestTrackingPackageParams = (
  block: TradeDetailLogisticsBlock,
): {
  logisticsPackageList: LogisticsPackage[];
  isThird?: boolean;
} | null => {
  const { payload } = block;

  if (payload.kind === 'common') {
    return {
      logisticsPackageList: payload.logisticsList,
    };
  }

  if (payload.kind === 'commonThird') {
    return {
      logisticsPackageList: payload.logisticsList,
      isThird: true,
    };
  }

  if (payload.kind === 'hisense') {
    return {
      logisticsPackageList: payload.logisticsList,
      isThird: true,
    };
  }

  if (payload.kind === 'lazada') {
    return {
      logisticsPackageList: payload.logisticsList,
    };
  }

  return null;
};

const LatestLogisticsDescriptionRow = ({
  block,
  conversation,
  row,
}: {
  block: TradeDetailLogisticsBlock;
  conversation?: Conversation.Conversation;
  row: NonNullable<TradeDetailLogisticsBlock['detailRows']>[number];
}) => {
  const { t } = useTranslate();
  const params = getLatestTrackingPackageParams(block);

  if (!params) {
    return <LogisticsDetailRow row={row} />;
  }

  return (
    <LatestTranslatableLogisticsDescriptionRow
      conversation={conversation}
      params={params}
      row={row}
      t={t}
    />
  );
};

const LatestTranslatableLogisticsDescriptionRow = ({
  conversation,
  params,
  row,
  t,
}: {
  conversation?: Conversation.Conversation;
  params: NonNullable<ReturnType<typeof getLatestTrackingPackageParams>>;
  row: NonNullable<TradeDetailLogisticsBlock['detailRows']>[number];
  t: ReturnType<typeof useTranslate>['t'];
}) => {
  const { displayTranslation, translating, translationError, onToggleTranslation } =
    useLogisticsListBusiness({
      conversation,
      logisticsPackageList: params.logisticsPackageList,
      isThird: params.isThird,
    });

  const latestTracking = params.logisticsPackageList[0]?.trackingInfo?.[0];
  const translatedDescription = getTranslation(latestTracking?.descriptionTranslation || {});
  const value = displayTranslation
    ? translatedDescription || latestTracking?.description || row.value
    : latestTracking?.description || row.value;

  const action = (() => {
    if (translating) {
      return (
        <span
          className="flex-shrink-0"
          title={t('scene.chatWorkspace.orderTab.logistics.action.translating')}
        >
          <LoadingOutlined />
        </span>
      );
    }

    if (translationError) {
      return (
        <span
          className="cursor-pointer flex-shrink-0"
          onClick={onToggleTranslation}
          title={t('scene.chatWorkspace.orderTab.logistics.action.translationError')}
        >
          <ReloadOutlined />
        </span>
      );
    }

    return (
      <ToolbarIcon
        size={14}
        icon="translate"
        className={
          displayTranslation
            ? 'text-[var(--fett-scenario-accent-text)] flex-shrink-0'
            : 'flex-shrink-0'
        }
        onClick={onToggleTranslation}
      />
    );
  })();

  return (
    <LogisticsDetailItem>
      <span>{row.label}</span>
      <span>
        <Tooltip title={value} placement="topRight">
          <span className="logistics-detail-value-text">{value}</span>
        </Tooltip>
        {action}
      </span>
    </LogisticsDetailItem>
  );
};

const renderLogisticsDetailRows = (
  block: TradeDetailLogisticsBlock,
  conversation?: Conversation.Conversation,
) => {
  if (!block.detailRows?.length) {
    return null;
  }

  return block.detailRows.map((row) =>
    row.id === 'latestDescription' ? (
      <LatestLogisticsDescriptionRow
        key={`${block.id}_${row.id}`}
        block={block}
        conversation={conversation}
        row={row}
      />
    ) : (
      <LogisticsDetailRow key={`${block.id}_${row.id}`} row={row} />
    ),
  );
};

const HisenseLogisticsActions = ({
  mediaItems,
  screenshotPackages,
  region,
}: {
  mediaItems?: MediaItem[];
  screenshotPackages: {
    trackingNumber?: string;
    trackingInfo: { updateTime: string | number; description: string }[];
  }[];
  region: General.UpperCaseRegion;
}) => {
  const { t } = useTranslate();
  const sendFileImage = useSendFileImage();

  return (
    <div className={cx('flex justify-end gap-3', actionsGroup)}>
      {mediaItems && mediaItems.length > 0 && (
        <MediaViewer
          items={mediaItems}
          trigger={
            <Button type="link" size="small">
              {t('scene.chatWorkspace.orderTab.logistics.action.viewShippingPhotos')}
            </Button>
          }
          toolbarRender={({ images, index, onClose }) => {
            const item = images[index];
            if (item.src) {
              return (
                <div
                  className="flex items-center gap-1 text-[14px] cursor-pointer hover:text-[#cecece] mr-6"
                  onClick={() => {
                    if (item.src) {
                      sendFileImage(item.src);
                      onClose?.();
                    }
                  }}
                >
                  <Send size={14} />
                  {t('scene.chatWorkspace.messageEditor.action.sendImage')}
                </div>
              );
            }
          }}
        />
      )}
      <SendLogisticsInfo
        logisticsPackages={screenshotPackages}
        btnText={t('scene.chatWorkspace.orderTab.logistics.action.generateScreenshot')}
        region={region}
      />
    </div>
  );
};

const renderLogisticsBlock = (
  block: TradeDetailLogisticsBlock,
  conversation?: Conversation.Conversation,
) => {
  const { payload } = block;
  const isHisense = payload.kind === 'hisense';

  const hisenseLogistics = isHisense ? payload.logisticsList : undefined;

  const mediaItems: MediaItem[] | undefined = hisenseLogistics
    ?.flatMap((logistics) => logistics.trackingInfo || [])
    .flatMap((tracking) => tracking.attachment || [])
    .map((item) => ({
      type: 'image' as const,
      src: item.url,
      thumb: item.url,
    }))
    .filter((item) => item.src);

  const screenshotPackages =
    isHisense && payload.logisticsList
      ? payload.logisticsList.map((logistics) => ({
          trackingNumber: logistics.trackingNumber,
          trackingInfo: logistics.trackingInfo.map((tracking) => ({
            updateTime: tracking.updateTime,
            description: tracking.description,
          })),
        }))
      : [];

  const info = (
    <LogisticsItem key={block.id}>
      <span>{block.title}</span>
      <div>
        {block.trackingLabel}
        <Popover
          trigger="click"
          arrowPointAtCenter
          placement="bottomRight"
          content={renderLogisticsContent(block)}
        >
          <PopoverTrigger>
            <DetailImg src={detailSvg} alt="detail" />
          </PopoverTrigger>
        </Popover>
      </div>
    </LogisticsItem>
  );

  const content = (
    <>
      {info}
      {renderLogisticsDetailRows(block, conversation)}
      {isHisense && conversation && (
        <HisenseLogisticsActions
          mediaItems={mediaItems}
          screenshotPackages={screenshotPackages}
          region={conversation.region}
        />
      )}
    </>
  );

  if (payload.kind !== 'common' || !payload.showSendLogistics || !conversation) {
    return <div key={`${block.id}_with_detail`}>{content}</div>;
  }

  return (
    <div key={`${block.id}_with_send`}>
      {content}
      <LogisticsSend>
        <SendLogistics conversation={conversation} logisticsList={payload.logisticsList} />
      </LogisticsSend>
    </div>
  );
};

function TradeDetailClassicView(props: TradeDetailClassicViewProps) {
  const { trade, blocks, conversation, storeType, ...otherProps } = props;

  const contentList = blocks
    .map((block) => {
      if (block.type === 'amount') {
        return renderAmountBlock(block, trade, storeType);
      }

      if (block.type === 'text') {
        return renderTextBlock(block);
      }

      return renderLogisticsBlock(block, conversation);
    })
    .filter((item) => item);

  return contentList.length > 0 ? <div {...otherProps}>{contentList}</div> : null;
}

export default memo(TradeDetailClassicView);

const actionsGroup = css`
  .ant-btn-sm {
    font-size: 12px;
  }
`;
